// src/analytics/analytics.ts
import {getAccessToken} from '../api/axiosConfig';
import {getAnonId} from '../utils/anon';

/** ===== Types ===== */
type EventPayload = {
  type: string;
  path?: string;
  category?: string;
  itemId?: string;
  label?: string;
  ms?: number;
  ts: number;
  anon_id: string;
  user_id?: string | number | null;
  extra?: Record<string, any>;
};

export type OnboardingAnalyticsExtra = {
  tourId?: string;
  stepIndex?: number;
  stepId?: string;
  target?: string;
  path?: string;
  device?: 'desktop' | 'mobile';
  elapsedMs?: number;
  timestamp?: number;
  [key: string]: unknown;
};

export type UxEventExtra = Record<string, string | number | boolean | null | undefined>;

export type AuthLoginAttempt = {
  provider: string;
  source?: string;
  startedAt: number;
};

/** ===== Internal state ===== */
const QUEUE: EventPayload[] = [];
const MAX_QUEUE = 1000;
const HEARTBEAT_INTERVAL_MS = 15_000;
const ANALYTICS_DEVICE_ID_KEY = 'policy-intel:analytics-device-id';
const ANALYTICS_DEVICE_COOKIE = 'pi_analytics_device';
const ANALYTICS_DEVICE_MAX_AGE_SEC = 60 * 60 * 24 * 365;
const AUTH_ATTEMPT_KEY = 'policy-intel:analytics-auth-attempt';
const AUTH_ATTEMPT_MAX_AGE_MS = 10 * 60 * 1000;
const LINK_DEDUPE_MS = 5000;

// P0-5 funnel analytics: browser/session attribution + D0/D1/D3/D7 retention.
const FUNNEL_SESSION_ID_KEY = 'policy-intel:funnel-session-id:v1';
const FUNNEL_ATTRIBUTION_KEY = 'policy-intel:funnel-attribution:v2';
const FUNNEL_LEGACY_ATTRIBUTION_KEY = 'policy-intel:funnel-attribution:v1';
const FUNNEL_SESSION_ATTRIBUTION_KEY = 'policy-intel:funnel-session-attribution:v1';
const FUNNEL_FIRST_LANDING_DATE_KEY = 'policy-intel:funnel-first-landing-date-kst:v1';
const FUNNEL_SESSION_MILESTONE_PREFIX = 'policy-intel:funnel-milestone:v1:';

let flushTimer: number | undefined;
let heartbeatTimer: number | undefined;

let trackedPagePath = '';
let lastTrackedPagePath = '';
let lastPageViewAtMs = 0;
let pageVisibleStartedAtMs = 0;
let pageAccumulatedVisibleMs = 0;
let dwellStartMs = 0;
let cachedAnalyticsDeviceId = '';
let currentPageInstanceId = '';

let linkInFlight: Promise<boolean> | null = null;
let lastLinkedAnonId = '';
let lastLinkedAtMs = 0;

const uxDedupeAt = new Map<string, number>();
const PAGE_VIEW_DEDUPE_MS = 1000;
const UX_LIFECYCLE_DEDUPE_MS = 1000;
const MIN_MAIN_EXIT_MS = 1000;
const FUNNEL_CANONICAL_DEDUPE_MS = 1000;
const FUNNEL_CANONICAL_EVENTS = new Set([
  'landing_view', 'return_d1', 'return_d3', 'return_d7',
  'market_preview_open', 'guest_stock_open', 'stock_detail_view', 'track_stock_click',
  'auth_modal_open', 'auth_method_selected', 'signup_complete', 'login_complete',
  'watch_stock_add', 'portfolio_stock_add',
  'judgement_view', 'why_view', 'validation_view',
  'push_prompt_view', 'push_enabled', 'notification_open',
  // P0 Value Judgment product funnel. These names are the new product authority;
  // legacy aliases remain above for historical dashboards.
  'stock_search', 'value_judgment_view', 'value_reason_expand', 'stock_track_click',
  'login_after_value_view',
  'stock_track_created', 'stock_track_restored', 'stock_track_already_active',
  // historical read-only alias. 신규 코드는 더 이상 stock_track_success를 발생시키지 않는다.
  'stock_track_success',
  'value_change_view', 'return_visit',
  'pro_offer_view', 'subscription_start',
]);

/** ===== Utilities ===== */
function nowMs() {
  return typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now();
}

function getUserId(): string | number | null {
  try {
    const raw = localStorage.getItem('userId');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  } catch {
    return null;
  }
}

function getCurrentPath(): string {
  if (typeof window === 'undefined' || !window.location) return '/';
  const {pathname, search, hash} = window.location;
  return `${pathname || ''}${search || ''}${hash || ''}` || '/';
}

function getDocumentReferrer(): string | null {
  if (typeof document === 'undefined') return null;
  const value = String(document.referrer || '').trim();
  return value || null;
}

function getCurrentAccessToken(): string | null {
  try {
    const token = getAccessToken();
    return typeof token === 'string' && token.trim() ? token.trim() : null;
  } catch {
    return null;
  }
}

function scheduleFlush(delay = 2000) {
  if (typeof window === 'undefined') return;
  if (flushTimer) window.clearTimeout(flushTimer);
  flushTimer = window.setTimeout(flush, delay);
}

/**
 * 익명 이벤트는 sendBeacon을 우선 사용한다.
 * 로그인 이벤트는 반드시 Authorization 헤더를 붙일 수 있는 fetch를 사용한다.
 *
 * 서버는 payload의 user_id를 신뢰하지 않고 JWT principal의 userId만 저장하므로,
 * 로그인 상태에서 Authorization 헤더가 빠지면 analytics_event.user_id가 NULL이 된다.
 */
function sendBeaconOrFetch(url: string, body: string) {
  const token = getCurrentAccessToken();

  if (token && typeof fetch !== 'undefined') {
    void fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body,
      keepalive: true,
    }).catch(() => {
      // analytics 실패가 서비스 UX를 막으면 안 된다.
    });
    return;
  }

  if (typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
    const ok = navigator.sendBeacon(
        url,
        new Blob([body], {type: 'application/json'}),
    );
    if (ok) return;
  }

  if (typeof fetch !== 'undefined') {
    void fetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      credentials: 'include',
      body,
      keepalive: true,
    }).catch(() => {
      // analytics 실패가 서비스 UX를 막으면 안 된다.
    });
  }
}

function currentVisibleMs(): number {
  const running = pageVisibleStartedAtMs > 0
      ? Math.max(0, nowMs() - pageVisibleStartedAtMs)
      : 0;

  return Math.max(0, Math.round(pageAccumulatedVisibleMs + running));
}

function pauseVisibleClock() {
  if (pageVisibleStartedAtMs <= 0) return;
  pageAccumulatedVisibleMs += Math.max(0, nowMs() - pageVisibleStartedAtMs);
  pageVisibleStartedAtMs = 0;
}

function resumeVisibleClock() {
  if (!trackedPagePath || pageVisibleStartedAtMs > 0) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  pageVisibleStartedAtMs = nowMs();
}

function startHeartbeat() {
  if (typeof window === 'undefined' || !trackedPagePath) return;

  stopHeartbeat();
  resumeVisibleClock();

  heartbeatTimer = window.setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }

    push({
      type: 'heartbeat',
      path: trackedPagePath,
      ms: currentVisibleMs(),
    });
  }, HEARTBEAT_INTERVAL_MS) as unknown as number;
}


type FunnelAttribution = {
  source?: string;
  traffic_source?: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  landing_variant?: string;
};

type FunnelAttributionContext = FunnelAttribution & {
  first_touch_source?: string;
  first_touch_referrer?: string;
  first_touch_utm_source?: string;
  first_touch_utm_medium?: string;
  first_touch_utm_campaign?: string;
  session_source?: string;
  session_referrer?: string;
  session_utm_source?: string;
  session_utm_medium?: string;
  session_utm_campaign?: string;
};

function randomId(prefix: string): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `${prefix}_${crypto.randomUUID()}`;
    }
  } catch {
    // fall through
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

function getFunnelSessionId(): string {
  if (typeof window === 'undefined') return 'server';
  try {
    const existing = sessionStorage.getItem(FUNNEL_SESSION_ID_KEY)?.trim();
    if (existing) return existing;
    const created = randomId('fs');
    sessionStorage.setItem(FUNNEL_SESSION_ID_KEY, created);
    return created;
  } catch {
    return randomId('fs');
  }
}

function isInternalHost(hostname: string): boolean {
  const host = String(hostname || '').trim().toLowerCase().replace(/\.$/, '');
  if (!host) return false;
  return host === 'policy-intel.com'
      || host === 'www.policy-intel.com'
      || host.endsWith('.policy-intel.com')
      || host === 'localhost'
      || host === '127.0.0.1';
}

function safeUrlAttribution(): FunnelAttribution {
  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search || '');
    const referrer = getDocumentReferrer();
    let referrerHost = '';
    try {
      referrerHost = referrer ? new URL(referrer).hostname : '';
    } catch {
      referrerHost = '';
    }
    const externalReferrerHost = isInternalHost(referrerHost) ? '' : referrerHost;
    const utmSource = params.get('utm_source')?.trim() || '';
    const explicitSource = params.get('source')?.trim() || '';
    const acquisitionSource = explicitSource || utmSource || externalReferrerHost || 'direct';
    return {
      source: acquisitionSource,
      traffic_source: acquisitionSource,
      referrer: referrer || undefined,
      utm_source: utmSource || undefined,
      utm_medium: params.get('utm_medium')?.trim() || undefined,
      utm_campaign: params.get('utm_campaign')?.trim() || undefined,
      utm_content: params.get('utm_content')?.trim() || undefined,
      utm_term: params.get('utm_term')?.trim() || undefined,
      landing_variant:
          params.get('landing_variant')?.trim()
          || params.get('variant')?.trim()
          || undefined,
    };
  } catch {
    return {source: 'direct', traffic_source: 'direct'};
  }
}

function parseStoredAttribution(raw: string | null): FunnelAttribution | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as FunnelAttribution;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function getFirstTouchAttribution(): FunnelAttribution {
  if (typeof window === 'undefined') return {};
  try {
    const current = parseStoredAttribution(localStorage.getItem(FUNNEL_ATTRIBUTION_KEY));
    if (current) return current;

    // v1 stored browser attribution is already closer to first-touch than any later referrer.
    // Migrate it once, but never preserve an internal referrer as acquisition authority.
    const legacy = parseStoredAttribution(localStorage.getItem(FUNNEL_LEGACY_ATTRIBUTION_KEY));
    if (legacy) {
      const legacySource = String(legacy.traffic_source || legacy.source || '').trim();
      const normalizedLegacy = /policy-intel\.com/i.test(legacySource)
          ? {...legacy, source: 'direct', traffic_source: 'direct'}
          : legacy;
      localStorage.setItem(FUNNEL_ATTRIBUTION_KEY, JSON.stringify(normalizedLegacy));
      return normalizedLegacy;
    }
  } catch {
    // continue with current URL/referrer
  }

  const initial = safeUrlAttribution();
  try {
    localStorage.setItem(FUNNEL_ATTRIBUTION_KEY, JSON.stringify(initial));
  } catch {
    // storage restrictions must not block the product
  }
  return initial;
}

function getSessionAttribution(): FunnelAttribution {
  if (typeof window === 'undefined') return {};
  try {
    const existing = parseStoredAttribution(sessionStorage.getItem(FUNNEL_SESSION_ATTRIBUTION_KEY));
    if (existing) return existing;
  } catch {
    // continue with current URL/referrer
  }

  const initial = safeUrlAttribution();
  try {
    sessionStorage.setItem(FUNNEL_SESSION_ATTRIBUTION_KEY, JSON.stringify(initial));
  } catch {
    // storage restrictions must not block the product
  }
  return initial;
}

function readCookie(name: string): string {
  if (typeof document === 'undefined') return '';
  const prefix = `${name}=`;
  const parts = String(document.cookie || '').split(';');
  for (const raw of parts) {
    const part = raw.trim();
    if (!part.startsWith(prefix)) continue;
    try {
      return decodeURIComponent(part.slice(prefix.length)).trim();
    } catch {
      return part.slice(prefix.length).trim();
    }
  }
  return '';
}

function writeAnalyticsDeviceCookie(value: string) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  try {
    const hostname = String(window.location?.hostname || '').toLowerCase();
    const domain = hostname === 'policy-intel.com' || hostname.endsWith('.policy-intel.com')
        ? '; Domain=.policy-intel.com'
        : '';
    const secure = window.location?.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${ANALYTICS_DEVICE_COOKIE}=${encodeURIComponent(value)}; Max-Age=${ANALYTICS_DEVICE_MAX_AGE_SEC}; Path=/${domain}; SameSite=Lax${secure}`;
  } catch {
    // Cookie failure must not block analytics/product UX.
  }
}

function getAnalyticsDeviceId(): string {
  if (cachedAnalyticsDeviceId) return cachedAnalyticsDeviceId;

  let value = '';
  try {
    value = String(localStorage.getItem(ANALYTICS_DEVICE_ID_KEY) || '').trim();
  } catch {
    // continue with cookie
  }

  if (!value) {
    value = readCookie(ANALYTICS_DEVICE_COOKIE);
  }

  if (!/^dev_[A-Za-z0-9._-]{8,120}$/.test(value)) {
    value = randomId('dev');
  }

  cachedAnalyticsDeviceId = value;

  try {
    localStorage.setItem(ANALYTICS_DEVICE_ID_KEY, value);
  } catch {
    // Cookie remains the cross-session authority when storage is unavailable.
  }
  writeAnalyticsDeviceCookie(value);

  return value;
}

function getFunnelAttributionContext(): FunnelAttributionContext {
  const firstTouch = getFirstTouchAttribution();
  const session = getSessionAttribution();
  return {
    ...firstTouch,
    source: firstTouch.source || firstTouch.traffic_source || 'direct',
    traffic_source: firstTouch.traffic_source || firstTouch.source || 'direct',
    first_touch_source: firstTouch.traffic_source || firstTouch.source || 'direct',
    first_touch_referrer: firstTouch.referrer,
    first_touch_utm_source: firstTouch.utm_source,
    first_touch_utm_medium: firstTouch.utm_medium,
    first_touch_utm_campaign: firstTouch.utm_campaign,
    session_source: session.traffic_source || session.source || 'direct',
    session_referrer: session.referrer,
    session_utm_source: session.utm_source,
    session_utm_medium: session.utm_medium,
    session_utm_campaign: session.utm_campaign,
  };
}

function buildFunnelContext(extra?: Record<string, any>): Record<string, any> {
  const attribution = getFunnelAttributionContext();
  const ticker = String(extra?.stock_code ?? extra?.ticker ?? '').trim();
  const explicitSource = String(extra?.source ?? '').trim();
  const explicitAuthTrigger = String(extra?.auth_trigger ?? extra?.authTrigger ?? '').trim();

  return {
    anonymous_id: getAnonId(),
    device_id: getAnalyticsDeviceId(),
    session_id: getFunnelSessionId(),
    page_instance_id: currentPageInstanceId || undefined,
    ...attribution,
    source: explicitSource || attribution.source || 'direct',
    auth_trigger: explicitAuthTrigger || undefined,
    stock_code: ticker || undefined,
  };
}

function kstDateKey(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const value = (type: string) => parts.find((part) => part.type === type)?.value || '';
  const year = value('year');
  const month = value('month');
  const day = value('day');
  return year && month && day ? `${year}-${month}-${day}` : '';
}

function utcDayFromDateKey(dateKey: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return null;
  return Math.floor(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / 86400000);
}

function markSessionMilestoneOnce(name: string): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const key = `${FUNNEL_SESSION_MILESTONE_PREFIX}${name}`;
    if (sessionStorage.getItem(key) === '1') return false;
    sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}

/** ===== Auth analytics helpers ===== */
export function markAuthLoginAttempt(provider: string, source?: string) {
  if (typeof window === 'undefined') return;

  const normalizedProvider = String(provider || '').trim().toLowerCase();
  if (!normalizedProvider) return;

  const payload: AuthLoginAttempt = {
    provider: normalizedProvider,
    source: source ? String(source).trim() : undefined,
    startedAt: Date.now(),
  };

  try {
    sessionStorage.setItem(AUTH_ATTEMPT_KEY, JSON.stringify(payload));
  } catch {
    // 저장소가 제한되어도 실제 인증은 계속 진행한다.
  }
}

export function consumeAuthLoginAttempt(): AuthLoginAttempt | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = sessionStorage.getItem(AUTH_ATTEMPT_KEY);
    sessionStorage.removeItem(AUTH_ATTEMPT_KEY);

    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<AuthLoginAttempt>;
    const provider = String(parsed.provider || '').trim().toLowerCase();
    const source = typeof parsed.source === 'string' ? parsed.source.trim() : undefined;
    const startedAt = Number(parsed.startedAt || 0);

    if (!provider || !Number.isFinite(startedAt) || startedAt <= 0) {
      return null;
    }

    if (Date.now() - startedAt > AUTH_ATTEMPT_MAX_AGE_MS) {
      return null;
    }

    return {
      provider,
      source: source || undefined,
      startedAt,
    };
  } catch {
    return null;
  }
}

/**
 * 로그인 직후 현재 anon_id를 JWT principal의 실제 userId에 연결한다.
 * body에는 userId를 보내지 않는다. 사용자 식별은 서버 principal만 신뢰한다.
 */
export function linkAnalyticsSession(): Promise<boolean> {
  if (typeof window === 'undefined' || typeof fetch === 'undefined') {
    return Promise.resolve(false);
  }

  const token = getCurrentAccessToken();
  if (!token) return Promise.resolve(false);

  const anonId = getAnonId();
  if (!anonId) return Promise.resolve(false);

  const now = Date.now();
  if (
      lastLinkedAnonId === anonId &&
      lastLinkedAtMs > 0 &&
      now - lastLinkedAtMs < LINK_DEDUPE_MS
  ) {
    return Promise.resolve(true);
  }

  if (linkInFlight) return linkInFlight;

  linkInFlight = fetch('/api/analytics/link-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify({anon_id: anonId}),
    keepalive: true,
  })
  .then((response) => {
    if (!response.ok) return false;
    lastLinkedAnonId = anonId;
    lastLinkedAtMs = Date.now();
    return true;
  })
  .catch(() => false)
  .finally(() => {
    linkInFlight = null;
  });

  return linkInFlight;
}

/**
 * Notification engagement attribution.
 *
 * Flow:
 * provider link -> /api/notification-engagement/c/{opaque token}
 * -> 302 destination?pi_nt={opaque token}
 * -> this function records OPEN only after the app has actually booted.
 *
 * The token is never copied into analytics_event.extra and is removed from the address bar
 * immediately after the OPEN request is scheduled.
 */
export function consumeNotificationOpenAttribution(): boolean {
  if (typeof window === 'undefined' || typeof fetch === 'undefined') return false;

  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return false;
  }

  const token = String(url.searchParams.get('pi_nt') || '').trim();
  if (!/^[A-Za-z0-9_-]{24,64}$/.test(token)) return false;

  url.searchParams.delete('pi_nt');
  const cleanRelativeUrl =
      `${url.pathname}${url.search}${url.hash}` || '/';

  try {
    window.history.replaceState(
        window.history.state,
        document.title,
        cleanRelativeUrl,
    );
  } catch {
    // Address-bar cleanup failure must not block the application.
  }

  void fetch('/api/notification-engagement/open', {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    credentials: 'include',
    body: JSON.stringify({trackingToken: token}),
    keepalive: true,
  }).catch(() => {
    // Engagement analytics must never block the destination UX.
  });

  trackUxEvent('notification_open', {
    source: 'notification',
    destination: cleanRelativeUrl,
  });
  return true;
}

/** ===== Core ===== */
function flush() {
  if (!QUEUE.length) return;
  const events = QUEUE.splice(0, QUEUE.length);
  sendBeaconOrFetch('/api/analytics/events', JSON.stringify({events}));
}

function push(ev: Omit<EventPayload, 'anon_id' | 'ts' | 'user_id'>) {
  if (QUEUE.length >= MAX_QUEUE) {
    QUEUE.splice(0, QUEUE.length - MAX_QUEUE + 1);
  }

  const eventExtra = ev.extra ?? {};
  const funnelContext = buildFunnelContext(eventExtra);

  QUEUE.push({
    anon_id: getAnonId(),
    user_id: getUserId(),
    ts: Date.now(),
    ...ev,
    extra: {
      ...funnelContext,
      ...eventExtra,
    },
  });

  scheduleFlush(2000);
}

/** ===== Public API ===== */
export function trackPageView(path?: string) {
  const p = path || getCurrentPath();
  const now = Date.now();

  // React.StrictMode 개발 재실행 또는 동일 경로 초단기 재호출은 한 번만 기록한다.
  if (
      p === lastTrackedPagePath &&
      lastPageViewAtMs > 0 &&
      now - lastPageViewAtMs < PAGE_VIEW_DEDUPE_MS
  ) {
    return;
  }

  lastPageViewAtMs = now;

  stopHeartbeat();
  pauseVisibleClock();

  const previousPath = lastTrackedPagePath || null;
  trackedPagePath = p;
  lastTrackedPagePath = p;
  currentPageInstanceId = randomId('pv');
  pageAccumulatedVisibleMs = 0;
  pageVisibleStartedAtMs = 0;
  resumeVisibleClock();

  push({
    type: 'page_view',
    path: p,
    extra: {
      referrer: getDocumentReferrer(),
      previousPath,
      previous_internal_path: previousPath,
      canonicalPath: p,
      page_instance_id: currentPageInstanceId,
    },
  });

  startHeartbeat();
}


/**
 * 현재 페이지의 실제 visible 누적 시간을 반환한다.
 * wall-clock 체류시간과 분리해 main engagement 계측에서 사용한다.
 */
export function getCurrentPageVisibleMs(): number {
  return currentVisibleMs();
}

/**
 * P0-5 retention authority for anonymous/browser cohorts.
 * - first landing date is fixed in KST
 * - landing_view is emitted once per browser session
 * - return_d1 / return_d3 / return_d7 are exact KST-day milestones
 * - authenticated linkage continues to use the existing linkAnalyticsSession() flow
 */
export function trackFunnelLandingView(extra: UxEventExtra = {}) {
  if (typeof window === 'undefined') return;

  const today = kstDateKey();
  let firstDate = today;
  try {
    const stored = localStorage.getItem(FUNNEL_FIRST_LANDING_DATE_KEY)?.trim();
    if (stored && utcDayFromDateKey(stored) !== null) {
      firstDate = stored;
    } else {
      localStorage.setItem(FUNNEL_FIRST_LANDING_DATE_KEY, today);
    }
  } catch {
    // storage restrictions must not block analytics or product UX
  }

  const firstDay = utcDayFromDateKey(firstDate);
  const currentDay = utcDayFromDateKey(today);
  const cohortDay = firstDay !== null && currentDay !== null
      ? Math.max(0, currentDay - firstDay)
      : 0;

  if (markSessionMilestoneOnce('landing_view')) {
    trackUxEvent('landing_view', {
      ...extra,
      cohort_day: cohortDay,
      first_landing_date: firstDate,
      visit_date: today,
    });
  }

  const retentionEvent = cohortDay === 1
      ? 'return_d1'
      : cohortDay === 3
          ? 'return_d3'
          : cohortDay === 7
              ? 'return_d7'
              : '';

  if (retentionEvent && markSessionMilestoneOnce(retentionEvent)) {
    trackUxEvent(retentionEvent, {
      ...extra,
      cohort_day: cohortDay,
      first_landing_date: firstDate,
      visit_date: today,
    });
  }
}

export function trackClick(
    category: string,
    itemId?: string,
    label?: string,
    extra?: Record<string, any>,
) {
  push({type: 'click', category, itemId, label, path: getCurrentPath(), extra});
}

export function trackUxEvent(
    eventName: string,
    payload: UxEventExtra = {},
) {
  const elapsedMs = typeof payload.elapsedMs === 'number' ? payload.elapsedMs : undefined;
  const stayMs = typeof payload.stayMs === 'number' ? payload.stayMs : undefined;
  const ms = elapsedMs ?? stayMs;
  const currentPath = getCurrentPath();

  // StrictMode의 effect setup -> cleanup -> setup으로 생기는 가짜 초단기 종료는 저장하지 않는다.
  if (
      eventName === 'main_exit' &&
      typeof stayMs === 'number' &&
      Number.isFinite(stayMs) &&
      stayMs < MIN_MAIN_EXIT_MS
  ) {
    return;
  }

  // 메인 lifecycle 진입/로드 이벤트는 같은 경로에서 1초 내 중복을 제거한다.
  if (eventName === 'main_view' || eventName === 'main_loaded') {
    const key = `${eventName}:${currentPath}`;
    const now = Date.now();
    const previousAt = uxDedupeAt.get(key) ?? 0;

    if (previousAt > 0 && now - previousAt < UX_LIFECYCLE_DEDUPE_MS) {
      return;
    }

    uxDedupeAt.set(key, now);
  }

  if (FUNNEL_CANONICAL_EVENTS.has(eventName)) {
    const ticker = String(payload.stock_code ?? payload.ticker ?? '').trim();
    const key = `funnel:${eventName}:${currentPath}:${ticker}`;
    const now = Date.now();
    const previousAt = uxDedupeAt.get(key) ?? 0;
    if (previousAt > 0 && now - previousAt < FUNNEL_CANONICAL_DEDUPE_MS) return;
    uxDedupeAt.set(key, now);
  }

  const itemId = typeof payload.target === 'string'
      ? payload.target
      : typeof payload.itemId === 'string'
          ? payload.itemId
          : undefined;

  const label = typeof payload.label === 'string'
      ? payload.label
      : typeof payload.destination === 'string'
          ? payload.destination
          : itemId;

  push({
    type: eventName,
    category: 'ux',
    itemId,
    label,
    path: currentPath,
    ms: typeof ms === 'number' && Number.isFinite(ms) ? Math.round(ms) : undefined,
    extra: {
      ...payload,
      eventName,
    },
  });
}

export function trackOnboardingEvent(
    eventName: string,
    extra: OnboardingAnalyticsExtra = {},
) {
  const stepIndex = typeof extra.stepIndex === 'number' ? extra.stepIndex : undefined;
  const elapsedMs = typeof extra.elapsedMs === 'number' ? Math.round(extra.elapsedMs) : undefined;

  push({
    type: eventName,
    category: 'onboarding',
    itemId: extra.stepId || extra.target,
    label: extra.target || extra.stepId,
    path: extra.path || getCurrentPath(),
    ms: elapsedMs,
    extra: {
      ...extra,
      stepIndex,
      elapsedMs,
    },
  });
}

export function startDwell(category: string, itemId?: string) {
  dwellStartMs = nowMs();
  push({type: 'dwell_start', category, itemId, path: getCurrentPath()});
}

export function stopDwell(category?: string, itemId?: string) {
  if (!dwellStartMs) return;

  const ms = Math.round(nowMs() - dwellStartMs);
  push({type: 'dwell_end', category, itemId, path: getCurrentPath(), ms});
  dwellStartMs = 0;
}

export function stopHeartbeat() {
  if (heartbeatTimer && typeof window !== 'undefined') {
    window.clearInterval(heartbeatTimer);
    heartbeatTimer = undefined;
  }
}

export function flushNow() {
  flush();
}

/** ===== Lifecycle hooks ===== */
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    try {
      pauseVisibleClock();
      stopDwell();
      stopHeartbeat();
      flush();
    } catch {
      // analytics lifecycle failure must never block navigation.
    }
  });

  window.addEventListener('beforeunload', () => {
    try {
      pauseVisibleClock();
      stopDwell();
      stopHeartbeat();
      flush();
    } catch {
      // no-op
    }
  });

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      try {
        if (document.visibilityState === 'hidden') {
          pauseVisibleClock();
          stopDwell();
          stopHeartbeat();
          flush();
          return;
        }

        // hidden 상태에서 heartbeat를 멈춘 뒤 다시 돌아오면 반드시 재개한다.
        resumeVisibleClock();
        startHeartbeat();
      } catch {
        // no-op
      }
    });
  }
}
